import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Row, Col, Container } from "react-bootstrap";
import { SpotActions } from "../../../../../redux/actions";
import { allSharedComponents } from "../../../../../shared";
import ViewSpotModal from "../../../../../shared/components/ViewSpotModal/ViewSpotModal";
import cancelIcon from "../../../../../assets/images/icon/crossIcon.svg";
import {
  changeTableSortIcons,
  debounce,
  getSortDirectionProperty,
} from "../../../../../system";
import {
  emptyString,
  modeConstants,
  translationConstants,
} from "../../../../../constants";
import { getcolumns } from "../utilities";

const { DataTable, CustomModal } = allSharedComponents;

const initialGridState = {
  length: 10,
  pageNumber: 1,
  orderBy: "id desc",
  filterQuery: "spotInfo.spotTypeId == 1",
  search: "",
};

const PrivateList = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const privateList = useSelector(
    (state) => state.SpotsReducer.spotsData?.pageData
  );
  const totalElements = useSelector(
    (state) => state.SpotsReducer.spotsData?.page?.totalElements
  );

  const sortingInProgressRef = useRef(false);
  const [isShow, setShow] = useState(false);
  const [mode, setMode] = useState(modeConstants.VIEW_MODE);
  const [rowData, setRowData] = useState(emptyString);
  const [gridState, setGridState] = useState(initialGridState);
  const [defaultPage, setDefaultPage] = useState(gridState.pageNumber);
  const [inputSearch, setInputSearch] = useState(emptyString);
  const searchInputRef = useRef(null);

  const onSetShow = () => {
    // dispatch(UserActions.OpenModal(false));
    setShow((prev) => !prev);
  };

  const getRowData = (data) => {
    onSetShow();
    dispatch(SpotActions.getSpotById(data?.id));
  };

  const [columns, setColumns] = useState(getcolumns(t, getRowData, setMode));

  const onSort = (sortedColumn) => {
    sortingInProgressRef.current = true;
    setColumns(changeTableSortIcons(columns, sortedColumn));
    const columnDirection = getSortDirectionProperty(sortedColumn);
    setDefaultPage(gridState.pageNumber);
    setGridState((prevState) => ({
      ...prevState,
      orderBy: columnDirection ? `${sortedColumn.id} ${columnDirection}` : null,
    }));
  };

  const onChangePage = (pageNumber) => {
    if (!sortingInProgressRef.current) {
      setGridState((prevState) => ({
        ...prevState,
        pageNumber: pageNumber,
      }));
    }
  };

  const onChangeRowsPerPage = (newPerPage, pageNumber) => {
    setGridState((prevState) => ({
      ...prevState,
      length: newPerPage,
      pageNumber: pageNumber,
    }));
  };

  useEffect(() => {
    setRowData(privateList);
  }, [privateList]);

  useEffect(() => {
    dispatch(SpotActions.getPaginatedSpots(gridState));
    sortingInProgressRef.current = false;
    // eslint-disable-next-line
  }, [gridState]);

  useEffect(() => {
    setColumns(getcolumns(t, getRowData, setMode));
    // eslint-disable-next-line
  }, [i18n.dir()]);

  useEffect(() => {
    setGridState((prevState) => ({
      ...prevState,
      search: inputSearch,
    }));
    // eslint-disable-next-line
  }, [inputSearch]);

  const handleSearch = debounce((value) => {
    setInputSearch(value);
  }, process.env.REACT_APP_SEARCH_DEBOUNCE_INTERVAL);

  const handleResetSearch = () => {
    setInputSearch("");
    setGridState((prevState) => ({
      ...prevState,
      search: "",
    }));
    searchInputRef.current.value = "";
  };

  return (
    <div className="tableContainer">
      <Container className="m-0 p-0 mw-100 pb-2">
        <Row>
          <Col
            md={3}
            xs={12}
            className="d-flex justify-content-sm-start justify-content-center py-2 px-2"
          >
            <div className="searchInputDiv gap-2 d-flex justify-content-between align-items-center w-100 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
              >
                <g clipPath="url(#clip0_610_3748)">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.4015 13.3185L16.7053 17.6223C16.8565 17.7737 16.9415 17.979 16.9414 18.193C16.9413 18.407 16.8562 18.6122 16.7049 18.7635C16.5535 18.9147 16.3482 18.9997 16.1342 18.9996C15.9202 18.9995 15.715 18.9144 15.5637 18.7631L11.2599 14.4593C9.97335 15.4558 8.35548 15.9247 6.73543 15.7707C5.11539 15.6167 3.61486 14.8513 2.53911 13.6302C1.46336 12.4091 0.893196 10.8241 0.944603 9.19754C0.99601 7.571 1.66513 6.02514 2.81584 4.87443C3.96655 3.72372 5.51241 3.0546 7.13895 3.0032C8.76549 2.95179 10.3505 3.52196 11.5716 4.59771C12.7927 5.67346 13.5581 7.17398 13.7121 8.79402C13.8661 10.4141 13.3972 12.0319 12.4007 13.3185H12.4015ZM7.3417 14.1993C8.61468 14.1993 9.83553 13.6936 10.7357 12.7934C11.6358 11.8933 12.1415 10.6725 12.1415 9.3995C12.1415 8.12652 11.6358 6.90567 10.7357 6.00554C9.83553 5.10541 8.61468 4.59972 7.3417 4.59972C6.06872 4.59972 4.84788 5.10541 3.94775 6.00554C3.04762 6.90567 2.54193 8.12652 2.54193 9.3995C2.54193 10.6725 3.04762 11.8933 3.94775 12.7934C4.84788 13.6936 6.06872 14.1993 7.3417 14.1993Z"
                    fill="#575756"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_610_3748">
                    <rect
                      width="15.9645"
                      height="15.9998"
                      fill="white"
                      transform="translate(0.941406)"
                    />
                  </clipPath>
                </defs>
              </svg>
              <input
                type="text"
                className="searchInput w-100"
                placeholder={t(translationConstants.Search)}
                onChange={(e) => handleSearch(e.target.value)}
                ref={searchInputRef}
              />

              {inputSearch && (
                <div
                  onClick={handleResetSearch}
                  className="cross cursor-pointer"
                >
                  <img width={16} src={cancelIcon} alt="cancel icon" />
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
      <DataTable
        columns={columns}
        items={rowData}
        onSort={onSort}
        gridState={gridState}
        paginationTotalRows={totalElements}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        paginationDefaultPage={defaultPage}
        paginationResetDefaultPage={true}
      />

      <CustomModal
        size="lg"
        scrollable={true}
        setShow={onSetShow}
        show={isShow}
        closeOnOutsideClick={ mode === modeConstants.VIEW_MODE}
      >
        <div className="">
          <ViewSpotModal
            setShow={setShow}
            isPrivateSpot
            isEditNotAllowed
            isAddMode={mode === modeConstants.ADD_MODE}
            isEditMode={mode === modeConstants.EDIT_MODE}
            isViewMode={mode === modeConstants.VIEW_MODE}
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default PrivateList;
